import { QueryClient } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";
import { routeTree } from "./routeTree.gen";
import { DEFAULT_LOCALE, localizePath, splitLocalePath, type Locale } from "./i18n/config";
import "./styles.css";

export type LocaleRef = { current: Locale };

export const getRouter = () => {
  // Ported from the old App.tsx: short stale window + refetch on focus/reconnect
  // so returning users always see fresh data instead of a long-lived snapshot.
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30 * 1000,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        retry: 1,
      },
    },
  });

  // One router instance is created per request on the server, so the active
  // locale can live on the instance itself.
  const localeRef: LocaleRef = {
    current:
      typeof window === "undefined"
        ? DEFAULT_LOCALE
        : splitLocalePath(window.location.pathname).locale,
  };

  const router = createRouter({
    routeTree,
    context: { queryClient, localeRef },
    scrollRestoration: true,
    defaultPreloadStaleTime: 0,
    // Swap to the new page immediately instead of holding the previous one
    // on screen while the next route loads.
    defaultPendingMs: 0,
    defaultPendingMinMs: 0,
    // Language versions live behind a URL prefix (/no, /zh-hant) while the
    // route tree itself stays language-free.
    rewrite: {
      input: ({ url }) => {
        const { locale, pathname } = splitLocalePath(url.pathname);
        localeRef.current = locale;
        const next = new URL(url);
        next.pathname = pathname;
        return next;
      },
      output: ({ url }) => {
        const next = new URL(url);
        next.pathname = localizePath(url.pathname, localeRef.current);
        return next;
      },
    },
  });

  // Streams the server-side query cache into the HTML so data fetched in a
  // loader is already present on first paint (no client refetch, no flash).
  setupRouterSsrQueryIntegration({ router, queryClient });

  return router;
};
